import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Send } from "lucide-react";

const inputCls = "w-full border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest";

export default function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [state, setState] = useState({ status: "idle", message: "" });

  const load = () => {
    api.get(`/admin/subscribers${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then(({ data }) => setSubs(data.subscribers)).catch(() => {});
    api.get("/admin/newsletters").then(({ data }) => setNewsletters(data.newsletters)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const createAndSend = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send "${draft.subject}" to all confirmed subscribers? This cannot be undone.`)) return;
    setState({ status: "loading", message: "" });
    try {
      const { data: nl } = await api.post("/admin/newsletters", draft);
      const { data } = await api.post(`/admin/newsletters/${nl.id}/send`);
      setState({ status: "success", message: `Sent to ${data.sent} subscriber(s)${data.failed ? `, ${data.failed} failed` : ""}.` });
      setDraft({ subject: "", body: "" });
      load();
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <div data-testid="admin-newsletter">
      <h1 className="font-serif text-3xl font-semibold text-forest">Newsletter</h1>
      <p className="mt-2 text-sm text-charcoal/70">
        Only subscribers who confirmed their email address (double opt-in) receive newsletters.
        Every email includes an unsubscribe link.
      </p>

      <form onSubmit={createAndSend} className="mt-8 bg-white border border-sandstone p-6 lg:p-8 space-y-4 max-w-3xl" data-testid="newsletter-compose-form">
        <h2 className="font-serif text-xl font-semibold text-forest flex items-center gap-2">
          <Send size={18} aria-hidden="true" /> Compose &amp; send
        </h2>
        <div>
          <label htmlFor="nl-subject" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Subject</label>
          <input id="nl-subject" required maxLength={200} value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className={inputCls} data-testid="newsletter-subject-input" />
        </div>
        <div>
          <label htmlFor="nl-body" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Message</label>
          <textarea id="nl-body" required rows={8} maxLength={20000} value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })} className={inputCls} data-testid="newsletter-body-input" />
        </div>
        <button type="submit" disabled={state.status === "loading"} data-testid="newsletter-send-button"
          className="bg-forest text-white text-sm font-medium px-6 py-2.5 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
          {state.status === "loading" ? "Sending…" : "Send to confirmed subscribers"}
        </button>
        {state.status === "success" && <p className="text-sm text-forest" data-testid="newsletter-send-success">{state.message}</p>}
        {state.status === "error" && <p className="text-sm text-red-700" role="alert">{state.message}</p>}
      </form>

      <h2 className="mt-10 font-serif text-2xl font-semibold text-forest">Past newsletters</h2>
      <div className="mt-4 bg-white border border-sandstone overflow-x-auto max-w-3xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-sandstone text-xs uppercase tracking-wider text-charcoal/60">
              <th className="px-5 py-3 font-semibold">Subject</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Sent</th>
            </tr>
          </thead>
          <tbody>
            {newsletters.map((n) => (
              <tr key={n.id} className="border-b border-sandstone/60 last:border-0">
                <td className="px-5 py-3">{n.subject}</td>
                <td className="px-5 py-3">{n.status}</td>
                <td className="px-5 py-3">{n.status === "sent" ? `${n.sent_count} delivered, ${n.failed_count} failed` : "—"}</td>
              </tr>
            ))}
            {newsletters.length === 0 && <tr><td colSpan={3} className="px-5 py-6 text-center text-charcoal/50">No newsletters yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-10 flex items-center gap-3 max-w-3xl">
        <h2 className="font-serif text-2xl font-semibold text-forest mr-auto">Subscribers</h2>
        <label htmlFor="sub-search" className="sr-only">Search subscribers</label>
        <input id="sub-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…"
          className="border border-sage bg-white px-3 py-2 text-sm rounded-sm" data-testid="subscriber-search-input" />
        <button onClick={load} className="text-sm bg-forest text-white px-4 py-2 hover:bg-forest-dark transition-colors duration-300" data-testid="subscriber-search-button">Search</button>
      </div>
      <div className="mt-4 bg-white border border-sandstone overflow-x-auto max-w-3xl">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead>
            <tr className="text-left border-b border-sandstone text-xs uppercase tracking-wider text-charcoal/60">
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Since</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.email} className="border-b border-sandstone/60 last:border-0">
                <td className="px-5 py-3">{s.email}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${s.status === "confirmed" ? "text-forest" : s.status === "pending" ? "text-gold-dark" : "text-charcoal/50"}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-charcoal/60">{new Date(s.created_at).toLocaleDateString("en-GB")}</td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan={3} className="px-5 py-6 text-center text-charcoal/50">No subscribers found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
